import type {
  AdminUsageDashboardResponse,
  AdminUsageFilterOption,
  AdminUsageModelFilterOption,
} from '@nadoutong/contracts';
import { Check, ChevronDown } from 'lucide-react';
import { type FocusEvent, useState } from 'react';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { DateRangePicker } from '../../ui/index.ts';
import type { DateRangePreset } from '../../ui/index.ts';
import { isModelOptionFor, modelOptionDimension } from './calibers.ts';
import type { ModelDimension } from './calibers.ts';
import { monthToDateRange, rollingDayRange, testIdSlug } from './format.ts';
import type { DayRange } from './format.ts';
import { MAX_RANGE_SPAN_DAYS } from './shared.tsx';
import type { Translate } from './shared.tsx';

/**
 * Every control that narrows the admin dashboard query, and nothing
 * else.
 *
 * The bar is fully controlled: it holds no filter state, it only
 * reports changes. `AdminUsage` owns the state because each change
 * also has to reset seat-table paging — a rule the bar must not be
 * able to forget or duplicate. The one piece of state that IS local is
 * the API-key popover's open/`showRevoked` flags, which are pure
 * presentation and never reach the query.
 *
 * `adminRangePresets` lives here rather than on the page so the window
 * choices and the widget that renders them cannot disagree; the page
 * imports it only to resolve the `range` URL param against the same
 * list. Preset ids are the literal URL values, so they are lowercase
 * and stable — renaming one changes a bookmarkable link, so the ids
 * stay untranslated and only the labels go through `t`.
 *
 * The admin window labels are their own `usage.admin.range.*` keys
 * rather than the shared `usage.range.*` ones, because the member
 * screen spells the same spans short (`7d`) and this one spells them
 * long (`Last 7 days`) — one key cannot hold both.
 */

/**
 * Granularity ids are the API's own values and the `data-testid`
 * suffixes; only the button face is translated. The map is exhaustive
 * by construction so a new granularity cannot render its raw id.
 */
const GRANULARITY_KEYS: Readonly<Record<'day' | 'week' | 'month', I18nKey>> = {
  day: 'usage.admin.granularity.day',
  week: 'usage.admin.granularity.week',
  month: 'usage.admin.granularity.month',
};

export function adminRangePresets(today: string, t: Translate): readonly DateRangePreset[] {
  return [
    { id: '7d', label: t('usage.admin.range.7d'), range: rollingDayRange(today, 7) },
    { id: '30d', label: t('usage.admin.range.30d'), range: rollingDayRange(today, 30) },
    { id: '90d', label: t('usage.admin.range.90d'), range: rollingDayRange(today, 90) },
    { id: 'month', label: t('usage.admin.range.month'), range: monthToDateRange(today) },
  ];
}

export function AdminFilterBar(props: {
  readonly dashboard: AdminUsageDashboardResponse | undefined;
  readonly range: DayRange;
  readonly rangePresets: readonly DateRangePreset[];
  readonly activePresetId: string | null;
  readonly granularity: 'day' | 'week' | 'month';
  readonly groupBy: 'model' | 'department' | 'member';
  readonly modelOptions: readonly AdminUsageModelFilterOption[];
  readonly modelSelection: ModelDimension;
  readonly apiKeyId: string;
  readonly department: string;
  readonly memberAccountId: string;
  readonly onRange: (range: DayRange, presetId: string | null) => void;
  readonly onGranularity: (value: 'day' | 'week' | 'month') => void;
  readonly onGroupBy: (value: string) => void;
  readonly onModel: (dimension: ModelDimension | null) => void;
  readonly onApiKey: (value: string) => void;
  readonly onDepartment: (value: string) => void;
  readonly onMember: (value: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div
      data-testid="usage-admin-filter-bar"
      className="flex flex-col gap-3 rounded-lg border border-line bg-card p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <DateRangePicker
          value={props.range}
          onChange={props.onRange}
          presets={props.rangePresets}
          activePresetId={props.activePresetId}
          maxSpanDays={MAX_RANGE_SPAN_DAYS}
          ariaLabel={t('usage.range.aria_label')}
          labels={{
            prevMonth: t('usage.range.prev_month'),
            nextMonth: t('usage.range.next_month'),
            presets: t('usage.range.presets_aria_label'),
          }}
          testId="usage-range-picker"
        />
        <ModelSelectControl
          label={t('usage.admin.filter.model')}
          selection={props.modelSelection}
          onChange={props.onModel}
          allLabel={t('usage.admin.filter.all_models')}
          options={props.modelOptions}
          testId="usage-filter-model"
        />
        <ApiKeySelectControl
          label={t('usage.admin.filter.api_key')}
          value={props.apiKeyId}
          onChange={props.onApiKey}
          allLabel={t('usage.admin.filter.all_keys')}
          showRevokedLabel={t('usage.admin.filter.show_revoked_keys')}
          activeLabel={t('usage.admin.filter.key_active')}
          revokedLabel={t('usage.admin.filter.key_revoked')}
          unknownLabel={t('usage.admin.filter.key_unknown')}
          emptyLabel={t('usage.admin.filter.no_active_keys')}
          options={props.dashboard?.filters.apiKeys}
          testId="usage-filter-api-key"
        />
        <SelectControl
          label={t('usage.admin.filter.department')}
          value={props.department}
          onChange={props.onDepartment}
          allLabel={t('usage.admin.filter.all_departments')}
          options={props.dashboard?.filters.departments ?? []}
          testId="usage-filter-department"
        />
        <SelectControl
          label={t('usage.admin.filter.member')}
          value={props.memberAccountId}
          onChange={props.onMember}
          allLabel={t('usage.admin.filter.all_members')}
          options={props.dashboard?.filters.members ?? []}
          testId="usage-filter-member"
        />
        <SelectControl
          label={t('usage.admin.filter.group_by')}
          value={props.groupBy}
          onChange={props.onGroupBy}
          options={[
            { id: 'model', label: t('usage.admin.group_by.model') },
            { id: 'department', label: t('usage.admin.group_by.department') },
            { id: 'member', label: t('usage.admin.group_by.member') },
          ]}
          testId="usage-filter-group-by"
        />
      </div>
      <div className="inline-flex w-fit items-center gap-1 rounded-lg bg-raised p-1">
        {(['day', 'week', 'month'] as const).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => props.onGranularity(item)}
            className={[
              'rounded-md px-3 py-1.5 text-caption font-medium uppercase transition-colors',
              props.granularity === item ? 'bg-card text-forest' : 'text-ink-low hover:text-ink',
            ].join(' ')}
            data-testid={`usage-granularity-${item}`}
          >
            {t(GRANULARITY_KEYS[item])}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Plain labelled `<select>` for the flat filter dimensions. The empty
 * string is the "all" sentinel end to end — the page turns it into an
 * omitted query field rather than a wildcard value.
 *
 * The wrapping `<label>` is what gives every one of these controls an
 * accessible name, so the grouping dimension uses this component too
 * even though it has no "all" state: `allLabel` is omitted there and
 * the sentinel option is simply not rendered, because a grouping of
 * "nothing" is not a query the dashboard can answer.
 *
 * The width clamp is load-bearing, not cosmetic. A native `<select>`
 * draws its intrinsic width from its WIDEST `<option>`, and the member
 * dimension's options are whole email addresses — at 375px that grew
 * this control to 558px, 212px of which sat outside an
 * `overflow-x: hidden` `<main>`: not painted and not scrollable to, with
 * no page-level scrollbar to rescue it. The wrapper therefore carries
 * `max-w-full` (never wider than the filter row) and `min-w-0` (a flex
 * item's automatic minimum size is its own content, which is precisely
 * what refused to shrink); the `<select>` keeps a `min-w-32` floor so a
 * short option list still reads as a control, and `truncate` so the
 * closed face ends in an ellipsis instead of a hard clip.
 *
 * Clamping the box costs nothing in distinguishability, which is why it
 * is preferred over shortening the option text: the OPEN list is a
 * native popup sized by the platform, not by this box, so every option
 * is still shown in full at the moment the reader is choosing between
 * them.
 */
function SelectControl(props: {
  readonly label: string;
  readonly value: string;
  readonly allLabel?: string;
  readonly options: readonly { id: string; label: string }[];
  readonly testId: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  const allLabel = props.allLabel;
  return (
    <label className="inline-flex h-10 min-w-0 max-w-full items-center gap-2 rounded-lg border border-line bg-raised px-3 text-body text-ink-low">
      <span className="shrink-0">{props.label}</span>
      <select
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className="min-w-32 max-w-full truncate bg-transparent font-medium text-ink focus-visible:outline-none"
        data-testid={props.testId}
      >
        {allLabel === undefined ? null : <option value="">{allLabel}</option>}
        {props.options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The model dimension's control. It looks exactly like the flat ones,
 * and is the same widget underneath, but it speaks in `(vendor, model)`
 * pairs rather than in one string.
 *
 * The reason is #1672: the dimension is keyed by vendor AND model, so
 * two vendors serving one model name are two separately selectable
 * rows. A `<select>` can only carry a string, so the option's `id` is
 * what rides in the DOM — but it rides as an opaque token. It is looked
 * up in the very list it came from and never taken apart, because the
 * contract states in so many words that the composite `vendor:model`
 * id must not be parsed. What leaves this control is the option's own
 * structured `vendor` and `model` fields, which is exactly what the
 * query takes.
 *
 * Selection is matched the same way round: the current dimension is
 * compared against each option's fields to find which row is selected,
 * so no code here ever composes an id to compare against either.
 */
function ModelSelectControl(props: {
  readonly label: string;
  readonly selection: ModelDimension;
  readonly allLabel: string;
  readonly options: readonly AdminUsageModelFilterOption[];
  readonly testId: string;
  readonly onChange: (dimension: ModelDimension | null) => void;
}): JSX.Element {
  const selected =
    props.selection.model.length === 0
      ? undefined
      : props.options.find((option) => isModelOptionFor(option, props.selection));
  return (
    <SelectControl
      label={props.label}
      value={selected?.id ?? ''}
      allLabel={props.allLabel}
      options={props.options}
      testId={props.testId}
      onChange={(id) => {
        const option = props.options.find((entry) => entry.id === id);
        props.onChange(option === undefined ? null : modelOptionDimension(option));
      }}
    />
  );
}

/**
 * API keys get a custom popover instead of a `<select>` because the
 * option list carries a lifecycle: revoked keys still own historical
 * usage, so they must be reachable, but they would otherwise bury the
 * active keys an operator is actually filtering by. They are therefore
 * hidden behind an opt-in toggle, and hiding them again clears a
 * revoked selection so the visible controls never contradict the
 * query.
 *
 * The toggle governs the keys the operator has NOT chosen. The current
 * selection is exempt and always listed, revoked or not: `?apiKeyId=`
 * is a shareable deep link, so the trigger can arrive already showing a
 * revoked key, and a list that omits it leaves the operator unable to
 * confirm or re-pick what the page is filtered by. That exemption is
 * also why the toggle still defaults to off on such a link — the one
 * revoked key that matters is visible without unfolding the rest.
 *
 * A deep link can also name an id the server did not list at all.
 * `filters.apiKeys` is the org's entire key roster — revoked rows
 * included, unrelated to usage — so an absent id belongs to no key of
 * this org. The query still carries it and the dashboard still comes
 * back filtered down to nothing, which is why such a selection gets a
 * synthetic row of its own instead of falling through to the "all"
 * face: a control reading "all keys" over an empty dashboard invites
 * exactly the wrong conclusion. It renders the raw id, because that is
 * the only thing known about it, and the operator can pick "all keys"
 * to get out. The revoked toggle does not govern it — it is neither
 * active nor revoked.
 *
 * That verdict needs the roster to have arrived, which is why
 * `options` is optional rather than defaulted to `[]`: the dashboard
 * query holds no previous data across a filter change, so a perfectly
 * valid key spends every refetch with no roster to be found in.
 * Calling it "not found" then would be the same lie pointed the other
 * way, so an undelivered roster shows the id plainly and claims
 * nothing about it.
 *
 * Dismissal is a container-level blur that ignores focus moves INTO
 * the popover, so keyboard traversal through the checkbox and the
 * option list does not close it mid-use.
 *
 * All labels arrive as props — this component holds no copy of its
 * own, which keeps it ready to be translated from the call site.
 */
function ApiKeySelectControl(props: {
  readonly label: string;
  readonly value: string;
  readonly allLabel: string;
  readonly showRevokedLabel: string;
  readonly activeLabel: string;
  readonly revokedLabel: string;
  readonly unknownLabel: string;
  readonly emptyLabel: string;
  readonly options: readonly AdminUsageFilterOption[] | undefined;
  readonly testId: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const listedOption = props.options?.find((option) => option.id === props.value);
  const selection = apiKeySelection(props.value, props.options);
  const statusLabels: Readonly<Record<ApiKeyStatus, string>> = {
    active: props.activeLabel,
    revoked: props.revokedLabel,
    unknown: props.unknownLabel,
  };
  const unknownRows: readonly ApiKeyRow[] =
    selection === 'unknown'
      ? [
          {
            id: props.value,
            label: props.value,
            status: 'unknown',
            // Not `testIdSlug(id)`: `?apiKeyId=all` slugs to the same
            // suffix as the "all keys" row and duplicates the test id.
            testId: `${props.testId}-option-unknown`,
          },
        ]
      : [];
  const visibleOptions: readonly ApiKeyRow[] = [
    ...unknownRows,
    ...(props.options ?? [])
      .filter(
        (option) => showRevoked || !isRevokedApiKeyOption(option) || option.id === props.value,
      )
      .map((option) => ({
        id: option.id,
        label: option.label,
        status: isRevokedApiKeyOption(option) ? ('revoked' as const) : ('active' as const),
        testId: `${props.testId}-option-${testIdSlug(option.id)}`,
      })),
  ];

  function handleBlur(event: FocusEvent<HTMLDivElement>): void {
    const nextFocus = event.relatedTarget;
    // React types `relatedTarget` as `(EventTarget & Element) | null`,
    // which is why the `as Node` this replaced was accepted — and why
    // it was never doing anything. The type is optimistic: at runtime
    // focus can leave for `window`, an EventTarget that is no Node, and
    // `Node.contains` throws on one per WebIDL. Narrow instead of
    // asserting, and read a non-Node as "focus left the dropdown".
    if (nextFocus instanceof Node && event.currentTarget.contains(nextFocus)) return;
    setOpen(false);
  }

  function setShowRevokedKeys(next: boolean): void {
    setShowRevoked(next);
    if (!next && selection === 'revoked') {
      props.onChange('');
    }
  }

  function selectValue(next: string): void {
    props.onChange(next);
    setOpen(false);
  }

  return (
    <div className="relative inline-flex min-w-0 max-w-full" onBlur={handleBlur}>
      <button
        type="button"
        // Same clamp as `SelectControl`, for the same reason: a badge
        // and a full key id can push this face past the viewport, and
        // an unnamed key now carries a badge where it carried none.
        className="inline-flex h-10 min-w-72 max-w-full items-center gap-2 rounded-lg border border-line bg-raised px-3 text-body text-ink-low transition-colors hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        data-testid={props.testId}
      >
        <span>{props.label}</span>
        <span
          className={[
            'min-w-0 flex-1 truncate text-left font-medium text-ink',
            // A raw id is an opaque identifier, not a name.
            listedOption === undefined && selection !== null ? 'font-mono' : '',
          ].join(' ')}
        >
          {selection === null ? props.allLabel : (listedOption?.label ?? props.value)}
        </span>
        {selection === null || selection === 'pending' ? null : (
          <ApiKeyStatusBadge badge={{ label: statusLabels[selection], status: selection }} />
        )}
        <ChevronDown aria-hidden="true" className="h-4 w-4 text-ink-low" />
      </button>
      {open ? (
        <div
          className="absolute left-0 top-11 z-30 w-80 overflow-hidden rounded-lg border border-line bg-card"
          data-testid={`${props.testId}-menu`}
        >
          <label className="flex cursor-pointer items-center gap-2 border-b border-line px-3 py-2 text-caption font-medium text-ink-mid hover:bg-active">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(event) => setShowRevokedKeys(event.currentTarget.checked)}
              className="h-4 w-4 accent-forest"
              data-testid={`${props.testId}-show-revoked`}
            />
            <span>{props.showRevokedLabel}</span>
          </label>
          <div role="listbox" className="max-h-72 overflow-y-auto p-1" aria-label={props.label}>
            <ApiKeyOptionButton
              value=""
              label={props.allLabel}
              selected={props.value.length === 0}
              onSelect={selectValue}
              testId={`${props.testId}-option-all`}
            />
            {visibleOptions.length === 0 ? (
              <p
                className="px-3 py-3 text-caption text-ink-low"
                data-testid={`${props.testId}-empty`}
              >
                {props.emptyLabel}
              </p>
            ) : (
              visibleOptions.map((option) => (
                <ApiKeyOptionButton
                  key={option.id}
                  value={option.id}
                  label={option.label}
                  selected={props.value === option.id}
                  onSelect={selectValue}
                  badge={{ label: statusLabels[option.status], status: option.status }}
                  testId={option.testId}
                />
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One row of the key popover; the check column is reserved even when unselected so labels stay aligned. */
function ApiKeyOptionButton(props: {
  readonly value: string;
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: (value: string) => void;
  readonly testId: string;
  // One object, so a caller cannot name a status without picking one:
  // a defaulted status would paint a revoked key as active, which is
  // the misstatement this control exists to prevent.
  readonly badge?: ApiKeyBadge;
}): JSX.Element {
  return (
    <button
      type="button"
      role="option"
      aria-selected={props.selected}
      onClick={() => props.onSelect(props.value)}
      className={[
        'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-body transition-colors',
        props.selected ? 'bg-forest text-on-forest' : 'text-ink hover:bg-active',
      ].join(' ')}
      data-testid={props.testId}
    >
      <span className="inline-flex w-5 justify-center" aria-hidden="true">
        {props.selected ? <Check className="h-4 w-4 text-on-forest" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium">{props.label}</span>
      {props.badge === undefined ? null : (
        <ApiKeyStatusBadge badge={props.badge} selected={props.selected} />
      )}
    </button>
  );
}

/** Lifecycle badge; on a selected row it inverts so it stays legible on the forest fill. */
function ApiKeyStatusBadge(props: {
  readonly badge: ApiKeyBadge;
  readonly selected?: boolean;
}): JSX.Element {
  return (
    <span
      className={[
        'shrink-0 rounded-md px-1.5 py-0.5 text-mono-label font-medium',
        props.selected === true
          ? 'bg-on-forest/20 text-on-forest'
          : STATUS_BADGE_TONE[props.badge.status],
      ].join(' ')}
    >
      {props.badge.label}
    </span>
  );
}

/**
 * What the control can say about the key it is filtering by. `unknown`
 * is not a server state — it is the absence of one, and it reads as a
 * caution rather than a failure because the id came from the URL the
 * operator followed, not from anything the org did.
 */
type ApiKeyStatus = 'active' | 'revoked' | 'unknown';

/** A status the control is willing to state, with the copy to state it in. */
interface ApiKeyBadge {
  readonly label: string;
  readonly status: ApiKeyStatus;
}

interface ApiKeyRow {
  readonly id: string;
  readonly label: string;
  readonly status: ApiKeyStatus;
  readonly testId: string;
}

/**
 * `warn` needs its own tint to clear AA: on `active` it lands at
 * 4.41:1, under the 4.5 this badge's body-size type is held to.
 */
const STATUS_BADGE_TONE: Readonly<Record<ApiKeyStatus, string>> = {
  active: 'bg-active text-forest',
  revoked: 'bg-active text-bad',
  unknown: 'bg-warn-tint text-warn',
};

/**
 * What the control may say about its current value. `null` means
 * nothing is selected — the only case the "all keys" face may be
 * shown. `pending` means the roster has not arrived, so the value can
 * be shown but not judged. Once the roster is in, a value it does not
 * contain is `unknown`, never "all": the query is filtered by it
 * either way.
 */
type ApiKeySelection = ApiKeyStatus | 'pending' | null;

function apiKeySelection(
  value: string,
  roster: readonly AdminUsageFilterOption[] | undefined,
): ApiKeySelection {
  if (value.length === 0) return null;
  if (roster === undefined) return 'pending';
  const listed = roster.find((option) => option.id === value);
  if (listed === undefined) return 'unknown';
  return isRevokedApiKeyOption(listed) ? 'revoked' : 'active';
}

/** A key is revoked when the server sent a non-empty `revokedAt`. */
function isRevokedApiKeyOption(option: AdminUsageFilterOption): boolean {
  return typeof option.revokedAt === 'string' && option.revokedAt.length > 0;
}
